import { useEffect } from 'react'
import { Routes, Route, Navigate, useParams, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { Header } from '@/components/layout'
import {
  HomePage,
  IlMondoNashiArganPage,
  FAQPage,
  ContattaciPage,
  CategoryCapelliPage,
  CategoryNovitaPage,
  CategoryCorpoPage,
  CategoryVisoPage,
  CategoryFragranzePage,
  CategoryUomoPage,
  ProductDetailPage,
  PrivacyPolicyPage,
  CookiePolicyPage,
  NashiLoversClubPage,
  NewsPage,
  DiventaPartnerPage,
  LandingCampagnaPage,
  StoreLocatorPage,
  StoreDetailPage,
  SaloniPage,
  SitemapPage,
  CondizioniWelcomeBoxPage,
  RegaliAziendaliPage,
  RegolamentoPage,
  EGiftCardPage,
  LavoraConNoiPage,
  NashiStorePage,
  NashiPartnerPage,
  WelcomeBoxPage,
  LoginPage,
} from '@/pages'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [pathname])
  return null
}

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang)
      }
    }
  }, [lang, i18n])

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  return (
    <>
      <ScrollToTop />
      <Header />
      <Outlet />
    </>
  )
}

export function LanguageRouter() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang" element={<LanguageWrapper />}>
        <Route index element={<HomePage />} />
        <Route path="il-mondo-nashi-argan" element={<IlMondoNashiArganPage />} />
        <Route path="the-world-of-nashi-argan" element={<IlMondoNashiArganPage />} />
        <Route path="faq" element={<FAQPage />} />
        <Route path="contattaci" element={<ContattaciPage />} />
        <Route path="contact-us" element={<ContattaciPage />} />

        {/* Welcome Box — dedicated page */}
        <Route path="welcome-box" element={<WelcomeBoxPage />} />

        {/* Login */}
        <Route path="login" element={<LoginPage />} />

        {/* Products — IT */}
        <Route path="prodotti" element={<Navigate to="novita" replace />} />
        <Route path="prodotti/novita" element={<CategoryNovitaPage />} />
        <Route path="prodotti/capelli" element={<CategoryCapelliPage />} />
        <Route path="prodotti/corpo" element={<CategoryCorpoPage />} />
        <Route path="prodotti/viso" element={<CategoryVisoPage />} />
        <Route path="prodotti/fragranze" element={<CategoryFragranzePage />} />
        <Route path="prodotti/uomo" element={<CategoryUomoPage />} />
        <Route path="prodotti/:slug" element={<ProductDetailPage />} />

        {/* Products — EN */}
        <Route path="products" element={<Navigate to="new" replace />} />
        <Route path="products/new" element={<CategoryNovitaPage />} />
        <Route path="products/hair" element={<CategoryCapelliPage />} />
        <Route path="products/body" element={<CategoryCorpoPage />} />
        <Route path="products/face" element={<CategoryVisoPage />} />
        <Route path="products/fragrances" element={<CategoryFragranzePage />} />
        <Route path="products/man" element={<CategoryUomoPage />} />
        <Route path="products/:slug" element={<ProductDetailPage />} />

        {/* Legal */}
        <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="cookie-policy" element={<CookiePolicyPage />} />

        {/* Loyalty */}
        <Route path="nashilovers-club" element={<NashiLoversClubPage />} />

        {/* News */}
        <Route path="news" element={<NewsPage />} />

        {/* Partner */}
        <Route path="diventa-partner" element={<DiventaPartnerPage />} />
        <Route path="become-a-partner" element={<DiventaPartnerPage />} />

        {/* Landing campagna — Spring Collection 2026 */}
        <Route path="nashi-spring-collection-2026" element={<LandingCampagnaPage />} />

        {/* Store Locator */}
        <Route path="store-locator" element={<StoreLocatorPage />} />
        <Route path="store-locator/:slug" element={<StoreDetailPage />} />

        {/* Saloni */}
        <Route path="saloni" element={<SaloniPage />} />
        <Route path="salons" element={<SaloniPage />} />

        {/* Nashi Store */}
        <Route path="nashi-store" element={<NashiStorePage />} />

        {/* Nashi Partner */}
        <Route path="nashi-partner" element={<NashiPartnerPage />} />

        {/* Sitemap */}
        <Route path="sitemap" element={<SitemapPage />} />

        {/* Condizioni Welcome Box */}
        <Route path="condizioni-generali-di-vendita-welcome-box" element={<CondizioniWelcomeBoxPage />} />
        <Route path="welcome-box-terms" element={<CondizioniWelcomeBoxPage />} />

        {/* Regali Aziendali */}
        <Route path="regali-aziendali" element={<RegaliAziendaliPage />} />
        <Route path="corporate-gifts" element={<RegaliAziendaliPage />} />

        {/* Regolamento */}
        <Route path="regolamento" element={<RegolamentoPage />} />
        <Route path="regulations" element={<RegolamentoPage />} />

        {/* e-Gift Card */}
        <Route path="e-gift-card" element={<EGiftCardPage />} />

        {/* Lavora con noi */}
        <Route path="lavora-con-noi" element={<LavoraConNoiPage />} />
        <Route path="work-with-us" element={<LavoraConNoiPage />} />
      </Route>
    </Routes>
  )
}
